<script setup lang="ts">
import { A11y } from 'swiper/modules'
import SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { CarouselNavigationType } from '~/helpers/datalayer/types'
import { SwiperOptions } from 'swiper/types'
import { RichTextPanelProps } from '../RichTextPanel/RichTextPanel.vue'

export type ProductCarouselProps = {
  label: string
  title?: string
  description?: string
  skUs: string[]
  preset?: 'column-panel'
  customSwiperOptions?: SwiperOptions
  /** `sizes` for the tiles when customSwiperOptions changes the slide width; see productImageSizes. */
  imageSizes?: string
  supportingData?: {
    home: {
      width?: {
        value: string
      }
      action?: {
        value?: 'addToBag'
      }
    }
  }
  productCarouselCta?: {
    fields?: Pick<RichTextPanelProps, 'content'>
  }
}
const props = defineProps<ProductCarouselProps>()
const width = props.supportingData?.home.width?.value
const action = props.supportingData?.home.action?.value

const i18n = useI18n()
const { getProducts } = useProducts()
const { isLoggedIn } = useUserStore()
// Fewer than three products in a wide column panel are stretched to fill it.
const isStretchedColumnPanel = props.preset === 'column-panel' && props.skUs.length < 3 && width === 'wide'
const imageSizes =
  props.imageSizes ?? (isStretchedColumnPanel ? productImageSizes.stretchedCarousel : productImageSizes.carousel)
const swiperOptions: SwiperOptions = {
  modules: [A11y],
  slidesPerView: 'auto',
  spaceBetween: 4,
  centerInsufficientSlides: true,
  breakpoints: {
    768: {
      slidesPerView:
        props.preset === 'column-panel'
          ? // edge case for if there are less then 3 products and the width is wide to enlarge the products in the column panel
            isStretchedColumnPanel
            ? props.skUs.length
            : 3
          : 4,
      slidesPerGroup: props.preset === 'column-panel' ? 3 : 4,
    },
  },
  ...props.customSwiperOptions,
}

const products = ref()
const loading = ref(true)
const swiperUtils = ref<SwiperCore | null>(null)
const isPrevDisabled = computed(() => swiperUtils.value?.isBeginning)
const isNextDisabled = computed(() => swiperUtils.value?.isEnd)
const showArrows = computed(() => {
  if (!swiperUtils.value) {
    // prevents flickering buttons in UI
    return false
  }
  return !(isPrevDisabled.value && isNextDisabled.value)
})
const swiperGaData = ref<{ type?: 'arrow' | 'swipe'; direction?: 'next' | 'prev' }>({})

const onSwiper = (swiper: SwiperCore) => {
  swiperUtils.value = swiper
}

const onSlideChange = (swiper: SwiperCore) => {
  const type = swiperGaData.value.type ?? 'swipe'
  const direction = swiperGaData.value.direction
  swiperGaData.value = {}
  if (direction) {
    // make sure to only send data layer when user navigates, not when loaded #221486
    sendProductCarouselDataLayerEvent(swiper, type, direction)
  }
}

const sendProductCarouselDataLayerEvent = (
  swiper: SwiperCore,
  type: CarouselNavigationType,
  direction?: 'next' | 'prev',
) => {
  pushEventToDataLayer(
    'custom.carousel.navigate',
    {
      group: props.title,
      type: type,
      direction: direction ?? swiper?.swipeDirection,
      info: {
        title: products.value?.[swiper.activeIndex]?.name,
        position: `${swiper.activeIndex}`,
        count: swiper.slides.length,
      },
    },
    i18n.locale.value,
  )
}

const navigateSwiper = (direction: 'next' | 'prev') => {
  if (!swiperUtils.value) {
    return
  }
  if (direction === 'next') {
    swiperGaData.value = { type: 'arrow', direction: 'next' }
    swiperUtils.value.slideNext()
  } else {
    swiperGaData.value = { type: 'arrow', direction: 'prev' }
    swiperUtils.value.slidePrev()
  }
}

onMounted(async () => {
  const fetchedProducts = await getProducts(props.skUs)
  if (fetchedProducts && !(fetchedProducts instanceof Error)) {
    products.value = fetchedProducts
      .filter((product) => product.inventory?.orderable)
      .map((product) => mapSfccProductDataToProductTile(product, i18n.locale.value))
  }
  loading.value = false
})
</script>
<template>
  <div
    v-if="loading || products"
    class="product-carousel"
    data-testid="product-carousel"
    :class="{
      'product-carousel--wide': width === 'wide',
      'product-carousel--column-panel': props.preset === 'column-panel',
    }"
    :data-carousel-title="title"
  >
    <div class="product-carousel__top-section">
      <h2 v-if="title" class="product-carousel__title heading-2">{{ title }}</h2>
      <p v-if="description" class="product-carousel__description">{{ description }}</p>
      <RichTextPanel v-if="productCarouselCta?.fields?.content" :content="productCarouselCta.fields.content" />
      <div v-if="showArrows" class="product-carousel__arrow-container">
        <button
          class="product-carousel__arrow-carousel-left"
          @click="navigateSwiper('prev')"
          :disabled="isPrevDisabled"
          :aria-label="$t('articleSwiper.button.previous')"
        >
          <svg-icon name="mhj:arrow-carousel-left" />
        </button>
        <button
          class="product-carousel__arrow-carousel-right"
          @click="navigateSwiper('next')"
          :disabled="isNextDisabled"
          :aria-label="$t('articleSwiper.button.next')"
        >
          <svg-icon name="mhj:arrow-carousel-right" />
        </button>
      </div>
    </div>
    <div v-if="loading" class="product-carousel__skelton-loader">
      <ProductTileSkeleton v-for="_ in 4" />
    </div>
    <!-- @vue-skip -->
    <Swiper
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      @tap="(swiper) => sendProductCarouselDataLayerEvent(swiper, 'thumb')"
      v-bind="swiperOptions"
    >
      <SwiperSlide v-for="(product, index) in products" :key="index" class="product-carousel__swiper-slide">
        <ProductTile
          v-bind="product"
          :list-name="title"
          :index="index"
          :total-products="products.length"
          :open-in-new-tab="false"
          :action="action"
          :is-authenticated="isLoggedIn"
          :image-sizes="imageSizes"
        />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="postcss">
.product-carousel.product-carousel--wide {
  @media (--desktop-large) {
    max-width: 80%;
  }
}
.product-carousel.product-carousel--column-panel {
  align-content: center;
  height: 100%;
  margin-bottom: 0;
  padding-top: var(--space-50);
  width: 100%;
  max-width: 100%;
  @media (--desktop) {
    padding-top: 0;
    padding-left: var(--space-70);
    padding-right: var(--space-70);
  }
}
.product-carousel {
  max-width: var(--container-max-width);
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--component-space);
  padding-left: var(--space-50);
  padding-right: var(--space-50);

  .product-carousel__top-section {
    &:has(.product-carousel__arrow-container):not(:has(.product-carousel__title), + .rich-text-panel) {
      height: 2.5rem;
    }
    &:has(.product-carousel__description) {
      display: grid;
      grid-template-columns: 1fr auto;
      margin-bottom: 0;
      @media (--tablet) {
        row-gap: 0;
        position: relative;
      }
    }
    .rich-text-panel {
      margin-bottom: 0;
      @media (--mobile-only) {
        padding-inline: unset;
      }
      .container {
        padding-inline: unset;
      }
    }
    &:has(.product-carousel__arrow-container) {
      .rich-text-panel {
        padding-right: var(--space-120);
        padding-left: var(--space-120);
        @media (--mobile-only) {
          padding-left: 0;
        }
      }
    }

    &:has(+ .rich-text-panel) {
      gap: 0;
    }

    &:not(:has(+ .rich-text-panel)) {
      grid-template-columns: 1fr;
    }

    display: flex;
    margin-bottom: var(--space-50);
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    @media (--tablet) {
      position: relative;
      justify-content: center;
    }
    @media (--mobile-only) {
      align-items: flex-start;
    }
  }

  .product-carousel__title {
    margin-bottom: 0;
    grid-column: 1 / -1; /* Span across both columns */
    @media (--tablet) {
      align-self: center;
      justify-self: center;
      margin-bottom: var(--space-90);
    }
    @media (--mobile-only) {
      align-self: flex-start;
    }
  }
  &:has(.product-carousel__arrow-container) {
    .product-carousel__title,
    .rich-text-panel {
      padding-right: var(--space-120);
      padding-left: var(--space-120);
      @media (--mobile-only) {
        padding-left: 0;
      }
    }
  }

  .link-text.anchor.action {
    text-underline-offset: unset;
    @media (--tablet) {
      display: unset;
    }
  }

  /* Product carousel inside column panel with cta */
  &.product-carousel--column-panel {
    .product-carousel__top-section {
      margin-bottom: var(--space-90) /* 48px */;
      @media (--mobile-only) {
        margin-bottom: var(--space-70) /* 32px */;
      }
      &:has(.product-carousel__title) {
        flex-direction: column;
      }
    }

    .product-carousel__title {
      &:has(+ .product-carousel__description, + .action-wrapper, + .rich-text-panel) {
        margin-bottom: var(--space-60);
      }
    }

    .product-carousel__description {
      margin-bottom: var(--space-50) /* 16px */;
      &:not(:has(+ .action-wrapper)):not(:has(+ .rich-text-panel)) {
        margin-bottom: 0;
      }
    }
    &:has(.product-carousel__arrow-container) {
      .product-carousel__description {
        padding-right: var(--space-120);
        padding-left: var(--space-120);
        @media (--mobile-only) {
          padding-left: 0;
        }
      }
    }

    .rich-text-panel {
      h2 {
        margin-bottom: var(--space-60) /* 24px */;
      }
      .rich-text {
        /* Fix for rich text automatically added empty paragraph */
        p:last-of-type:empty {
          display: none;
          margin: 0;
          padding: 0;
        }
      }

      .action-wrapper {
        .rich-text__block-entry.action {
          @media (--desktop) {
            margin: 0 auto;
          }
          @media (--mobile-only) {
            margin: 0;
            margin-left: 0;
          }
        }
        .link-text.anchor.action {
          text-underline-offset: unset;
          @media (--tablet) {
            display: unset;
          }
        }
      }

      @media (--tablet) {
        text-align: center;
        align-items: center;
      }
    }

    .action-wrapper {
      margin-bottom: 0;
      @media (--tablet) {
        text-align: center;
      }
    }

    @media (--mobile-only) {
      .product-carousel__description {
        &:has(+ .action-wrapper) {
          grid-column: 1 / -1;
        }
      }
      .action-wrapper {
        margin-bottom: 0;
        margin-left: 0;
      }
    }

    .product-carousel__arrow-container {
      position: relative;
      margin: auto;
      margin-right: 0;
      @media (--tablet) {
        margin-bottom: calc(var(--space-70) * -1); /* -48px */
      }
      @media (--mobile-only) {
        margin-left: auto;
        margin-bottom: calc(var(--space-50) * -1); /* -16px */
      }
    }

    &:has(+ .rich-text-panel) {
      .product-carousel__arrow-container {
        margin-bottom: calc(var(--space-70) * -1); /* -48px */
      }
    }

    @media (--tablet) {
      .rich-text {
        text-align: center;
      }
    }
  }

  &:not(.product-carousel--column-panel) {
    .product-carousel__top-section {
      margin-bottom: var(--space-90) /* 48px */;
      @media (--mobile-only) {
        margin-bottom: var(--space-70) /* 32px */;
      }

      &:has(.product-carousel__title) {
        flex-direction: column;
      }
    }

    .product-carousel__title {
      margin-bottom: var(--space-60) /* 24px */;

      @media (--mobile-only) {
        align-self: flex-start;
        padding-left: 0;
      }
    }

    .product-carousel__description {
      margin-bottom: var(--space-50) /* 16px */;
      grid-column: 1 / -1;
    }
    &:has(.product-carousel__arrow-container) {
      .product-carousel__description {
        padding-right: var(--space-120);
        padding-left: var(--space-120);
        @media (--mobile-only) {
          padding-left: 0;
        }
      }
    }

    .action-wrapper {
      @media (--tablet) {
        text-align: center;
      }
    }

    .rich-text {
      h2 {
        margin-bottom: var(--space-60) /* 24px */;
      }
      .action {
        margin: 0 auto;
        @media (--mobile-only) {
          margin: 0;
        }
      }
      p:empty,
      p:blank {
        display: none;
      }
    }

    @media (--tablet) {
      .rich-text {
        text-align: center;
      }

      .product-carousel__arrow-container {
        bottom: calc(var(--space-30) * -1) /* -8px */;
        margin-bottom: calc(var(--space-60) * -1) /* -24px */;
      }
    }
  }

  .product-carousel__description {
    align-self: center;
    text-align: start;
    margin-bottom: var(--space-50);
    @media (--tablet) {
      text-align: center;
      margin-bottom: var(--space-90);
    }
  }

  .product-carousel__arrow-container {
    min-width: fit-content;
    display: flex;
    flex-wrap: nowrap;
    gap: var(--space-50);
    align-self: flex-end;
    margin-left: auto;
    @media (--tablet) {
      margin-left: 0;
      position: absolute;
      bottom: 0;
      right: 0;
    }
  }

  .product-carousel__skelton-loader {
    width: 100%;
    display: flex;
    gap: var(--space-20);
    flex-wrap: nowrap;
    overflow-x: hidden;
    .skeleton {
      min-width: 9.5rem;
    }
    @media (--tablet) {
      display: grid;
      gap: var(--space-20);
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .product-carousel__swiper-slide {
    /* this is required for `slidesPerView: 'auto'` to work */
    width: 9.5rem;
  }

  .product-carousel__arrow-carousel-right,
  .product-carousel__arrow-carousel-left {
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    &:disabled {
      fill: var(--ui-dark-10-t40);
      color: var(--ui-dark-10-t40);
      cursor: default;
    }
    &:hover:not(:disabled) {
      background-color: var(--surface-light-20);
    }
  }
}
</style>
